import type { DecideReason, FormBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { closureApprovalRefusal, submitWrite, writeRefusal } from './writes'

/**
 * The eight things an operator may do to an AML case.
 *
 * # Why this is a module of its own
 *
 * `aml.ts` reads. This writes, and the split follows the backend's own
 * (`admin_aml.go` / `admin_aml_escalation.go`) for the same reason: the rules
 * about what may be WRITTEN to a case are a different body of knowledge from
 * the rules about how a case READS when half its fields are missing, and
 * putting them in one file makes both harder to check.
 *
 * # The one thing every control here has in common
 *
 * **Not one of them reaches a conclusion of ours about a case.** That is the
 * shape of `internal/amlcase` and it is not incidental:
 *
 *   - `escalate` hands the case to the institution and the case rests there.
 *     There is no state after `escalated` representing a conclusion we reached.
 *   - `acknowledge` and `resolve` record what the INSTITUTION said. Neither
 *     moves the case; `resolve` in particular leaves it `escalated`, because
 *     the outcome is theirs and the case's own history is where their ending
 *     gets written down.
 *   - `propose-closure` has NO EFFECT. The case is not closed, nothing stops
 *     being watched, and it stays in the queue. The design this comes from was
 *     a red-team finding about approval gates on steps that had already taken
 *     effect.
 *   - `approve-closure` is the only call that closes anything, and it needs a
 *     second person.
 *
 * A console that drew any of these as finishing a case would be fabricating an
 * authority we do not have, in the one section where that is a regulatory
 * problem rather than a UX one.
 *
 * # Compliance holds every write; Support holds the reads
 *
 * `PermAMLCaseWrite` is granted to Compliance alone, while `PermAMLCaseRead`
 * reaches Support — "a support agent taking a call needs to see that a case
 * exists without being able to touch it". So a support session sees all eight
 * controls, disabled, each naming Compliance (law 18). That is the intended
 * outcome and not a leak: knowing an escalation path exists is the point.
 */

/** `aml.case.write` — every write here. Compliance only. */
const PERM_WRITE = 'aml.case.write'

/** `aml.escalation.package.read` — also Compliance, and narrower on purpose. */
export const PERM_PACKAGE_READ = 'aml.escalation.package.read'

const ROLE = 'Compliance'

/**
 * What a case is, as far as a control needs to know.
 *
 * Deliberately not `CaseRow`. A control needs the state, who raised it and who
 * proposed a closure; giving it the whole read would let a control start
 * depending on a field whose absence the read handles and the control does not.
 */
export interface CaseStanding {
  readonly ref: string
  readonly state: string
  readonly openedBy: string
  readonly proposedBy: string
}

/**
 * The states an action is available from, straight out of the domain.
 *
 * Mirrors `internal/amlcase` transition by transition, and the sentences below
 * are the domain's own rather than invented restatements of them. Where the
 * backend explains a refusal in a sentence an operator needs to read — "case
 * %s is with the institution; its outcome is theirs to give" — showing them
 * something else at the field and that at the 409 would be two answers to one
 * question.
 *
 * `closed` is absent from every row on purpose: it is terminal, `ErrTerminal`
 * refuses everything, and a closed case offers no control at all.
 */
function stateRefusal(c: CaseStanding, action: AMLAction): string {
  if (c.state === '') {
    return 'Disabled — this read did not establish what state the case is in, so this console cannot tell whether the action is available. Acting blind on an AML case is not something to offer.'
  }
  if (c.state === 'closed') {
    return 'Disabled — this case is closed. A closed case is terminal: nothing further can be recorded against it, including a note.'
  }
  if (AVAILABLE_FROM[action].includes(c.state)) return ''
  return `Disabled — ${UNAVAILABLE[action](c.state)}`
}

export type AMLAction =
  | 'note' | 'escalate' | 'acknowledge' | 'resolve'
  | 'propose' | 'withdraw' | 'approve'

const AVAILABLE_FROM: Readonly<Record<AMLAction, readonly string[]>> = {
  note: ['open', 'closure_proposed', 'escalated'],
  // Permitted from closure_proposed too: an operator who sees a proposed
  // closure and disagrees escalates rather than argues, and making them
  // withdraw first would be friction on the safe path.
  escalate: ['open', 'closure_proposed'],
  acknowledge: ['escalated'],
  resolve: ['escalated'],
  propose: ['open'],
  withdraw: ['closure_proposed'],
  approve: ['closure_proposed'],
}

const UNAVAILABLE: Readonly<Record<AMLAction, (state: string) => string>> = {
  note: (s) => `the case is ${s}, and a note can only be added to a case that is still running.`,
  escalate: (s) => `this case is already ${s}. It is with the institution and cannot be handed over twice.`,
  acknowledge: (s) => `the case is ${s}, not escalated. Only a case that was handed to the institution has a receipt of theirs to record.`,
  resolve: (s) => `the case is ${s}, not escalated. Only a case with the institution has an outcome of theirs to record.`,
  propose: (s) => s === 'escalated'
    ? 'this case is with the institution; its outcome is theirs to give, so there is no closure of ours to propose.'
    : 'this case already has a proposed closure. Withdraw it before proposing another.',
  withdraw: () => 'there is no proposed closure on this case to withdraw.',
  approve: () => 'no closure has been proposed on this case, so there is nothing to countersign.',
}

/**
 * The refusals that apply to every AML write, in the order they override.
 *
 * Standby and permission come from `writeRefusal` and are about the SESSION.
 * The state refusal is about the CASE. Session first, because an operator on a
 * standby cell is told the same thing on every control and does not need to
 * work out which of eight cases is in which state to discover it.
 */
function refusalFor(caller: Caller, c: CaseStanding, action: AMLAction, act: string): string {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, act)
  if (session !== '') return session
  return stateRefusal(c, action)
}

// ---------------------------------------------------------------------------
// Reason codes
// ---------------------------------------------------------------------------

/**
 * Law 21: a reason code from a managed list, never free text.
 *
 * Each act gets its own list. A shared one would offer "monitoring alert" as a
 * reason for approving a closure, and an audit row saying that is worse than a
 * blank: it reads as an explanation while explaining nothing.
 */
const OPEN_REASONS: readonly DecideReason[] = [
  ['transaction_monitoring_alert', 'An automated monitoring rule fired', ''],
  ['screening_hit', 'A sanctions or PEP screening hit needs review', ''],
  ['manual_review', 'Found while reviewing an account', ''],
  ['support_escalation', 'Raised by support out of a customer contact', ''],
  ['institution_request', 'The institution asked us to look at this', ''],
  ['periodic_review', 'A scheduled review of an existing relationship', ''],
]

const NOTE_REASONS: readonly DecideReason[] = [
  ['investigation_step', 'Recording a step taken in the investigation', ''],
  ['evidence_received', 'Something arrived that bears on the case', ''],
  ['customer_contact', 'The customer was contacted, or made contact', ''],
  ['internal_consultation', 'Discussed with somebody else here', ''],
  ['no_further_action_rationale', 'Setting out why nothing further is proposed', ''],
]

const ESCALATE_REASONS: readonly DecideReason[] = [
  ['suspicion_threshold_met', 'The activity meets the threshold for handover', ''],
  ['sanctions_exposure', 'A sanctions nexus we are not equipped to judge', ''],
  ['institution_mandate', 'This falls to the institution under the mandate', ''],
  ['unresolved_after_review', 'Reviewed here and not resolvable here', ''],
]

const ACKNOWLEDGE_REASONS: readonly DecideReason[] = [
  ['institution_confirmed_receipt', 'They confirmed they have it', ''],
  ['institution_reference_supplied', 'They gave us their own reference', ''],
]

const RESOLVE_REASONS: readonly DecideReason[] = [
  ['institution_reported_outcome', 'They told us how it ended', ''],
  ['institution_closed_enquiry', 'They told us the enquiry is closed', ''],
]

const PROPOSE_REASONS: readonly DecideReason[] = [
  ['no_suspicious_activity_found', 'Reviewed and nothing supports the alert', ''],
  ['alert_explained_by_activity', 'The activity is explained by known behaviour', ''],
  ['duplicate_case', 'The same subject is covered by another case', ''],
  ['customer_evidence_satisfactory', 'The customer supplied a satisfactory account', ''],
]

const WITHDRAW_REASONS: readonly DecideReason[] = [
  ['new_information', 'Something arrived that changes the picture', ''],
  ['proposal_premature', 'Proposed before the review was finished', ''],
  ['proposed_in_error', 'Proposed on the wrong case', ''],
  ['checker_disagreed', 'A second operator disagreed and it is being withdrawn', ''],
]

const APPROVE_REASONS: readonly DecideReason[] = [
  ['reviewed_and_agreed', 'Reviewed the case independently and agree it closes', ''],
  ['proposal_evidence_sufficient', 'The evidence on the case supports the proposal', ''],
]

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

/**
 * Raising a case. The only control here that is not about an existing one.
 *
 * `customer_id` is typed rather than chosen, because there is no customer
 * picker on this surface and inventing one would be a search endpoint this
 * console does not have. It is the id, and the field says so — an operator
 * raising an AML case has the customer in front of them.
 */
export function openCaseForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_WRITE, ROLE, 'Raising an AML case')
  return {
    t: 'form',
    title: 'Raise a case',
    lead: 'A case is the record of a review. Raising one starts the trail and puts the case in the queue; it restricts nothing, holds no money and tells the customer nothing.',
    fields: [
      {
        name: 'customer_id', label: 'Customer id', kind: 'text', required: true, mono: true,
        note: 'The numeric id from the customer record this concerns.',
      },
      {
        name: 'reason_code', label: 'Case reason code', kind: 'select', required: true,
        options: OPEN_REASONS.map(([code, gloss]) => [code, `${code} — ${gloss}`] as const),
        note: 'Recorded on the case itself, and separate from the audit reason below: this one says what the case is about and stays with it for its whole life.',
      },
    ],
    reasons: OPEN_REASONS,
    reasonNote: 'Recorded on the opening event of the trail.',
    auditLine: 'admin.aml.case.open · actor · aml_case:<ref> · reason',
    cta: 'Raise case',
    ctaNote: 'You will not be able to approve this case’s closure afterwards — the operator who raises a case may not countersign its closing.',
    blocked,
    ...(blocked === '' ? { submit: openCaseSubmit(api) } : {}),
  }
}

function openCaseSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>('/aml/cases', {
      customer_id: Number(v['customer_id'] ?? ''),
      reason_code: v['reason_code'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The case was raised',
    (answer) => {
      const ref = refOf(answer)
      return ref === '' ? [] : [['case', ref, 'now open and in the queue']]
    },
  )
}

/** A note. Any operator with the write permission, any state but closed. */
export function noteForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const blocked = refusalFor(caller, c, 'note', 'Adding a note to an AML case')
  return {
    t: 'form',
    title: 'Add a note',
    lead: 'An observation on the record. A note changes no state and moves the case nowhere — it is deliberately the least gated thing here, because a control that made contributing to a case difficult would push the working record into somebody’s inbox.',
    // No field of its own: `POST /notes` takes `{reason}` and nothing else, so
    // the reason note IS the note. A second textarea here would be typed into
    // and silently dropped, which is worse than not offering it — the operator
    // would watch their observation vanish into a 200.
    fields: [],
    reasons: NOTE_REASONS,
    reasonNote: 'This text is the note. Read by whoever picks the case up next, and by an auditor months from now — neither will have your screen in front of them.',
    auditLine: `admin.aml.case.note · actor · aml_case:${c.ref} · reason`,
    cta: 'Add note',
    ctaNote: 'Appended to the trail. Nothing here can edit or remove an entry once it is written.',
    blocked,
    ...(blocked === '' ? { submit: actSubmit(api, c.ref, 'notes', 'The note was added', ['note', 'appended to the trail']) } : {}),
  }
}

/**
 * Handing the case to the institution.
 *
 * # One operator, alone, and that is deliberate
 *
 * No checker on this one. Escalating is the conservative direction, and a
 * second approver would delay escalations — which is the harm such a control
 * would be trying to prevent. The maker-checker gate sits on the closure, where
 * the risk runs the other way.
 *
 * # The operator does not compose the package
 *
 * They supply a reason; the SERVER assembles what goes to the institution. An
 * operator hand-typing a handover is an operator who leaves things out under
 * time pressure, so there is no field here for its contents and there must not
 * be one.
 *
 * # There is no undo, and its absence is the design
 *
 * `internal/escalation` registers no withdrawal and no delete: "an escalation
 * raised in error is resolved by telling the institution and recording what
 * they said back. A withdrawal route would be a burial route." The confirmation
 * says so rather than letting an operator discover it afterwards.
 */
export function escalateForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const blocked = refusalFor(caller, c, 'escalate', 'Escalating an AML case')
  return {
    t: 'form',
    title: 'Escalate to the institution',
    lead: 'Hands the case to the e-money institution, who decide what happens next. This console assembles nothing and decides nothing: you give the reason, the server assembles the package, and the case rests with them.',
    fields: [],
    reasons: ESCALATE_REASONS,
    reasonNote: 'Say what was found, not what should be done about it — the conclusion is theirs. Goes on the escalation and in the audit row.',
    auditLine: `admin.aml.case.escalate · actor · aml_case:${c.ref} · reason`,
    cta: 'Escalate',
    ctaNote: 'One operator, alone — there is no countersignature on an escalation, because delaying one is the harm. It cannot be withdrawn afterwards: an escalation raised in error is settled by telling them and recording their answer.',
    blocked,
    confirm: c.ref === '' ? 'ESCALATE' : c.ref,
    ...(blocked === '' ? { submit: escalateSubmit(api, c.ref) } : {}),
  }
}

function escalateSubmit(api: ApiClient, ref: string) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(`${casePath(ref)}/escalate`, { reason: v['reason'] ?? '' }),
    'The case was escalated',
    (answer) => {
      const escalationRef = stringField(answer, 'escalation_ref')
      return escalationRef === ''
        ? [['case', ref, 'now with the institution']]
        : [['escalation', escalationRef, 'quote this to the institution'], ['case', ref, 'now with the institution']]
    },
  )
}

/**
 * Recording that the institution received it.
 *
 * Not a decision and not a state change — the case stays `escalated`. It is
 * recorded because "we sent it and heard nothing" and "we sent it and they
 * have it" are different facts, and only the first needs chasing.
 *
 * The institution's reference is REQUIRED by the handler, which refuses an
 * acknowledgement without one. That is the whole value of the record: an
 * acknowledgement with no reference cannot be quoted back at anyone.
 */
export function acknowledgeForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const blocked = refusalFor(caller, c, 'acknowledge', 'Recording an acknowledgement')
  return {
    t: 'form',
    title: 'Record their acknowledgement',
    lead: 'Records that the institution confirmed receipt. The case stays with them and nothing about it changes — this only distinguishes a handover they have from one that may have gone nowhere.',
    fields: [
      {
        name: 'institution_ref', label: 'Their reference', kind: 'text', required: true, mono: true,
        note: 'The reference THEY gave us, exactly as they gave it. Required: an acknowledgement nobody can quote back is not a record of anything.',
      },
    ],
    reasons: ACKNOWLEDGE_REASONS,
    reasonNote: 'Recorded against the acknowledgement in the trail.',
    auditLine: `admin.aml.case.acknowledge · actor · aml_case:${c.ref} · reason`,
    cta: 'Record acknowledgement',
    ctaNote: 'The case stays escalated. This is a receipt, not an outcome.',
    blocked,
    ...(blocked === '' ? { submit: acknowledgeSubmit(api, c.ref) } : {}),
  }
}

function acknowledgeSubmit(api: ApiClient, ref: string) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(`${casePath(ref)}/acknowledge`, {
      institution_ref: v['institution_ref'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The acknowledgement was recorded',
    // Echoed from what was SENT, because the handler answers `{case_ref,
    // action}` and nothing else. Reading the reference off the answer would
    // print a dash in the line the operator most wants to check, immediately
    // after typing the value it is supposed to be showing them.
    () => [
      ['their reference', v['institution_ref'] ?? '—', 'as recorded'],
      ['case', ref, 'still escalated — this changed no state'],
    ],
  )
}

/**
 * Recording the outcome THEY reported.
 *
 * # The options come from the server, and that is not an accident of plumbing
 *
 * `recordable_outcomes` is on the escalation read specifically "so it renders
 * the two recordable outcomes rather than a free-text box somebody types a
 * filing into". Hard-coding the pair here would be the second implementation
 * that field exists to prevent, and it would drift silently the day the set
 * changes. When the read did not supply them, this control is disabled — see
 * `outcomes` below.
 *
 * # What the closed set leaves out is the point
 *
 * Two values, and neither says whether the institution reported anything to
 * anyone. Whether a filing was made, and to whom, is confidential to them; a
 * field inviting an operator to record it would be a **tipping-off hazard** and
 * would also be us keeping a record of a filing that is not ours. The note
 * field's guidance says so at the field, because that is where somebody is
 * about to type it.
 *
 * # It does not close the case
 *
 * The case stays `escalated`. There is no state after it representing a
 * conclusion of ours, and the outcome is written into the case's own history so
 * that a replay months later shows how it ended.
 */
export function resolveForm(
  api: ApiClient, caller: Caller, c: CaseStanding, outcomes: readonly string[],
): FormBlock {
  const blocked = outcomes.length === 0
    ? 'Disabled — the recordable outcomes come from the escalation record and this read did not supply them. This console will not offer a hand-written outcome in their place: the set of outcomes is the server’s, and a second copy of it here would be wrong the day it changes.'
    : refusalFor(caller, c, 'resolve', 'Recording an institution outcome')

  return {
    t: 'form',
    title: 'Record their outcome',
    lead: 'Records what the institution reported back. The case stays escalated — this is their conclusion written into our history, not one of ours.',
    fields: [
      {
        name: 'outcome', label: 'What they reported', kind: 'select', required: true,
        options: outcomes.map((o) => [o, OUTCOME_GLOSS[o] ?? o] as const),
        note: 'The closed set the server recognises. There is no value here meaning they filed a report, and there is not meant to be.',
      },
      {
        name: 'note', label: 'What they said', kind: 'textarea', required: true,
        note: 'Their words about the outcome. Do NOT record whether they made a report, to whom, or anything they told you in confidence — that is theirs, and writing it here is a tipping-off risk as well as a record we should not hold.',
      },
    ],
    reasons: RESOLVE_REASONS,
    reasonNote: 'Recorded against the outcome in the trail.',
    auditLine: `admin.aml.case.resolve · actor · aml_case:${c.ref} · reason`,
    cta: 'Record outcome',
    ctaNote: 'The case stays escalated. Recording their ending is not us reaching one.',
    blocked,
    ...(blocked === '' ? { submit: resolveSubmit(api, c.ref) } : {}),
  }
}

const OUTCOME_GLOSS: Readonly<Record<string, string>> = {
  closed_by_institution: 'closed_by_institution — they reviewed it and it ends there',
  taken_forward_by_institution: 'taken_forward_by_institution — it is theirs now, under their own obligations',
}

function resolveSubmit(api: ApiClient, ref: string) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(`${casePath(ref)}/resolve`, {
      outcome: v['outcome'] ?? '',
      note: v['note'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'Their outcome was recorded',
    (v2) => [
      ['outcome', stringField(v2, 'outcome') || (v['outcome'] ?? '—'), 'as they reported it'],
      ['case', ref, 'still escalated — no state of ours changed'],
    ],
  )
}

/**
 * The maker's half of a closure. It does nothing, and saying so is the control.
 *
 * The case is not closed, nothing stops being watched, and it stays in the
 * queue. That is deliberate: the red-team finding this design comes from was an
 * approval gate bolted onto a step that had already taken effect.
 */
export function proposeClosureForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const blocked = refusalFor(caller, c, 'propose', 'Proposing a closure')
  return {
    t: 'form',
    title: 'Propose closing with no action',
    lead: 'The first of two signatures. Proposing changes nothing: the case stays open, stays watched and stays in the queue until a second compliance officer countersigns. If nobody does, it stays open.',
    fields: [],
    reasons: PROPOSE_REASONS,
    reasonNote: 'Read by the person deciding whether to countersign, who reviews the case independently. Give them what you found, not your conclusion.',
    auditLine: `admin.aml.case.propose_closure · actor · aml_case:${c.ref} · reason`,
    cta: 'Propose closure',
    ctaNote: 'You will not be able to approve this closure yourself. Anyone — including you — can withdraw it.',
    blocked,
    ...(blocked === ''
      ? { submit: actSubmit(api, c.ref, 'propose-closure', 'The closure was proposed', ['case', 'still open, now awaiting a second signature']) }
      : {}),
  }
}

/**
 * Taking a proposal back. Available to anyone, including the proposer.
 *
 * Withdrawing moves the case towards MORE scrutiny, and gating the safe
 * direction is the mistake this whole workflow is built around avoiding. So
 * there is no maker-checker refusal on this one, and its absence is deliberate
 * rather than an omission.
 */
export function withdrawClosureForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const blocked = refusalFor(caller, c, 'withdraw', 'Withdrawing a proposed closure')
  return {
    t: 'form',
    title: 'Withdraw the proposed closure',
    lead: 'Returns the case to open. Available to anyone who can work the case, including whoever proposed the closure — withdrawing moves towards more scrutiny, and that direction is never gated here.',
    fields: [],
    reasons: WITHDRAW_REASONS,
    reasonNote: 'Stays on the trail. A withdrawn proposal, and the reason for it, is part of how the case reads later.',
    auditLine: `admin.aml.case.withdraw_closure · actor · aml_case:${c.ref} · reason`,
    cta: 'Withdraw proposal',
    ctaNote: 'The case returns to open. The proposal stays on the trail — nothing here removes it.',
    blocked,
    ...(blocked === ''
      ? { submit: actSubmit(api, c.ref, 'withdraw-closure', 'The proposal was withdrawn', ['case', 'back to open']) }
      : {}),
  }
}

/**
 * The checker's half — the only call that closes a case.
 *
 * # Law 15, and the refusal is stated here rather than discovered at the 409
 *
 * Two people are barred: whoever proposed the closure, and whoever raised the
 * case. `closureApprovalRefusal` computes both against the actor encoding the
 * backend records, and the sentence lands in `blocked` so the operator reads it
 * at the control instead of after composing the whole thing.
 *
 * The session refusals come first — a standby cell or a missing role is true of
 * every control on the screen, and telling somebody they are the maker when the
 * cell would refuse anything at all sends them to argue with the wrong person.
 *
 * # A compliance team of one cannot close a case
 *
 * That is the control working, and it is worth stating rather than discovering.
 * For a regulated agent a second compliance officer is not optional, and the
 * alternative — a bypass for small teams — is the bypass that gets used.
 */
export function approveClosureForm(api: ApiClient, caller: Caller, c: CaseStanding): FormBlock {
  const session = refusalFor(caller, c, 'approve', 'Approving a closure')
  const blocked = session !== ''
    ? session
    : closureApprovalRefusal(caller, { openedBy: c.openedBy, proposedBy: c.proposedBy })

  return {
    t: 'form',
    title: 'Countersign the closure',
    lead: 'The second signature, and the only thing on this screen that closes a case. Closing is terminal: nothing further can be recorded against the case afterwards, including a note.',
    fields: [],
    reasons: APPROVE_REASONS,
    reasonNote: 'Your own reading of the case, not a restatement of the proposal. The point of a second signature is a second review.',
    auditLine: `admin.aml.case.approve_closure · actor · aml_case:${c.ref} · reason`,
    cta: 'Countersign and close',
    ctaNote: 'Terminal. The case leaves the queue and cannot be reopened from this console.',
    blocked,
    confirm: c.ref === '' ? 'CLOSE' : c.ref,
    ...(blocked === ''
      ? { submit: actSubmit(api, c.ref, 'approve-closure', 'The case was closed', ['case', 'closed — terminal']) }
      : {}),
  }
}

// ---------------------------------------------------------------------------
// Shared plumbing
// ---------------------------------------------------------------------------

/**
 * The four acts that take nothing but a reason.
 *
 * `note`, `propose-closure`, `withdraw-closure` and `approve-closure` post
 * `{reason}` and nothing else, and answer `{case_ref, action}`.
 *
 * # Why none of them adds a textarea of its own
 *
 * `Form` already composes `reason` from the code and the operator's note
 * through `serialiseReason`, and counts it against the server's 200-BYTE cap as
 * they type. An adapter adding a second prose field would send a key
 * `takeReason` never reads: the text would be dropped, the call would answer
 * 200, and the operator would watch their explanation disappear into a success.
 *
 * Joining it into `reason` here instead would be worse. The byte count shown at
 * the field would no longer measure the string that gets sent, so the control
 * would refuse reasons the server would have taken and take ones it refuses —
 * and the operator would have no way to see which.
 */
function actSubmit(
  api: ApiClient,
  ref: string,
  segment: string,
  heading: string,
  line: readonly [label: string, note: string],
) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(`${casePath(ref)}/${segment}`, { reason: v['reason'] ?? '' }),
    heading,
    () => [[line[0], ref === '' ? '—' : ref, line[1]]],
  )
}

function casePath(ref: string): string {
  return `/aml/cases/${encodeURIComponent(ref)}`
}

function stringField(answer: unknown, key: string): string {
  if (typeof answer !== 'object' || answer === null) return ''
  const v = (answer as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

function refOf(answer: unknown): string {
  const direct = stringField(answer, 'case_ref')
  return direct !== '' ? direct : stringField(answer, 'ref')
}
